import { httpsCallable } from '@firebase/functions';
import { functions } from './firebase';
import { createPrefixedLogger } from '@utils/logger';

const log = createPrefixedLogger('planUpdateLink');

/**
 * The two calls behind /update — the page an assigned community leader
 * lands on from an email, with no account and never expected to make one.
 * Both wrap already-deployed UNAUTHENTICATED callables
 * (functions/src/plans/planUpdateLink.ts): the signed token in the URL is
 * the caller's entire authority, the same arrangement invites.ts already
 * uses for validateInviteCode.
 */

export type PlanUpdateStatus = 'accepted' | 'done' | 'declined';

export interface PlanUpdateTask {
  assignmentId: string;
  featureName: string;
  description?: string;
  status: string;
}

export interface PlanUpdateContext {
  planId: string;
  planTitle: string;
  partyName: string;
  tasks: PlanUpdateTask[];
}

export interface SubmitPlanUpdateResult {
  updated: number;
  newsEligible: number;
}

/**
 * The reasons a token can fail to resolve.
 *
 * The server throws `permission-denied` for an expired, a revoked and a
 * forged token alike, so the code alone cannot tell them apart. It sends
 * the reason as a value in the error's `details`
 * (`PlanUpdateLinkFailure` in functions/src/plans/planUpdateLink.ts), which
 * is what this reads. Never match on the message: that is copy, and copy
 * gets edited.
 *
 * An unrecognized reason degrades to the invalid-link screen rather than to
 * the generic "something went wrong" one, because a link that did not work
 * is the more useful thing to say.
 */
export type PlanUpdateLinkError =
  | { kind: 'expired' }
  | { kind: 'replaced' }
  | { kind: 'invalid' }
  | { kind: 'not-found' }
  | { kind: 'malformed' }
  | { kind: 'unknown'; message: string };

function toPlanUpdateLinkError(error: unknown): PlanUpdateLinkError {
  const code = (error as { code?: string } | null)?.code;
  const message = (error as { message?: string } | null)?.message ?? '';
  const reason = (error as { details?: { reason?: string } } | null)?.details?.reason;
  switch (code) {
    case 'functions/permission-denied':
      if (reason === 'expired') return { kind: 'expired' };
      if (reason === 'replaced') return { kind: 'replaced' };
      if (reason === 'party-gone') return { kind: 'not-found' };
      return { kind: 'invalid' };
    case 'functions/not-found':
      return { kind: 'not-found' };
    case 'functions/invalid-argument':
      return { kind: 'malformed' };
    default:
      return { kind: 'unknown', message: message || 'Something went wrong. Please try again.' };
  }
}

/** Resolve a token to the plan, party and tasks it covers. */
export async function getPlanUpdateContext(token: string): Promise<PlanUpdateContext> {
  try {
    const callable = httpsCallable<{ token: string }, PlanUpdateContext>(
      functions,
      'getPlanUpdateContext'
    );
    const result = await callable({ token });
    return result.data;
  } catch (error) {
    log.error('Failed to load plan update context:', error);
    throw toPlanUpdateLinkError(error);
  }
}

export interface SubmitPlanUpdateArgs {
  token: string;
  updates: { assignmentId: string; status: PlanUpdateStatus }[];
  note?: string;
}

/** Report progress on the tasks a party link covers. */
export async function submitPlanUpdate(
  args: SubmitPlanUpdateArgs
): Promise<SubmitPlanUpdateResult> {
  try {
    const callable = httpsCallable<SubmitPlanUpdateArgs, SubmitPlanUpdateResult>(
      functions,
      'submitPlanUpdate'
    );
    const result = await callable(args);
    return result.data;
  } catch (error) {
    log.error('Failed to submit plan update:', error);
    throw toPlanUpdateLinkError(error);
  }
}
